'use client'

import { useGyymClient } from '@gyym/sdk-react'
import { useSnackbar } from '@gyym/ui'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/navigation'
import React from 'react'

import {
  type FormValues,
  PasswordRecoveryForm,
  type SubmitContext,
} from '@/components/Forms/PasswordRecoveryForm'
import { Breadcrumbs, Content, Heading, Page } from '@/components/Layouts/Page'
import { getValidationErrors } from '@/lib/form'
import { useAuthUser } from '@/modules/Auth'

export default function PasswordRecoveryPage() {
  const client = useGyymClient()
  const router = useRouter()
  const { authUser, isLoggedIn } = useAuthUser()
  const { openSnackbar } = useSnackbar()

  const onSubmit = React.useCallback(
    async ({ email }: FormValues, { setError }: SubmitContext) => {
      try {
        await client.auth.user.passwordResetEmail(email)
        openSnackbar({ message: 'メールを送信しました' })
        router.push(isLoggedIn ? '/me/profile' : '/login')
      } catch (e) {
        if (client.isHttpError(e) && e.status === 422) {
          const message = getValidationErrors(e).email?.[0]
          if (message) {
            setError('email', { type: 'serverError', message })
            return
          }
        }
        Sentry.captureException(e)
        openSnackbar({
          variant: 'error',
          message: 'メールの送信に失敗しました。',
        })
      }
    },
    [client, isLoggedIn, openSnackbar, router],
  )

  return (
    <Page>
      <div className="mx-auto mt-8 max-w-lg px-4 pc:px-6">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'TOP', href: '/' },
            { label: 'ヘルプ', href: '/help/password_recovery' },
          ]}
        />

        <Heading>ヘルプ</Heading>

        <Content>
          <div className="tracking-[0.6px]">
            <h2 className="text-center text-base font-normal">
              パスワードを忘れた・
              <br />
              パスワードを変更したい
            </h2>
            <p className="mx-1 mt-4 text-xs text-[#555555]">
              パスワード再設定のためのURLを、登録済みメールアドレスへお送りします。届いたメールからURLをクリックして、新しいパスワードを再設定してください。
            </p>
            <div className="mt-6">
              <PasswordRecoveryForm
                values={{ email: authUser?.email ?? '' }}
                onSubmit={onSubmit}
              />
            </div>
          </div>
          
        </Content>
      </div>
    </Page>
  )
}
